<template>
  <el-dialog
    @close="onDialogClosing"
    :width="'100%'"
    :fullscreen="true"
    :destroy-on-close="true"
    :lock-scroll="true"
    :close-on-click-modal="false"
    v-model="syncedModel"
    custom-class="dialog-ocr"
  >
    <template #title>
      <div class="flex justify-center items-center">
        <div class="label-round"></div>
      </div>
    </template>
    <div v-if="isEnabledBarcodeReadWithKeyenceHT" class="scan-view">
      <div class="keyenece-empty-scan-area">
        <el-button type="primary" class="full-button el-button--primary is-larger" @click="startKeyenceScan">
          <div class="action-button">
            <IconScanQR />
            {{ $t('please_scan_machine_qr') }}
          </div>
        </el-button>
      </div>

      <div>
        <div class="dectect-input">
          <el-input readonly class="text-center text-lg" v-model="scannedValue"></el-input>
        </div>
        <div class="step-button">
          <el-button
            :disabled="!scannedValue"
            type="warning"
            class="el-button--primary is-circle is-larger clear-button"
            @click="resetScannedValue"
          >
            {{ $t('clear') }}
            <KeyenceHTHardKeyButton
              v-if="isEnabledBarcodeReadWithKeyenceHT"
              :hardkey="KeyenceHardKey.BACKSPACE"
              :function="resetScannedValue"
              :visible="syncedModel"
              :newLayer="true"
            />
          </el-button>
          <el-button class="el-button--primary is-circle is-larger" @click="confirmText(scannedValue)">
            {{ $t('ok') }}
            <KeyenceHTHardKeyButton
              v-if="isEnabledBarcodeReadWithKeyenceHT"
              :hardkey="KeyenceHardKey.ENTER"
              :function="() => confirmText(scannedValue)"
              :visible="syncedModel"
              :newLayer="false"
            />
          </el-button>
        </div>
      </div>
    </div>
    <component v-else :is="component.item" v-bind="component.props" @update:confirm="confirmText" />
  </el-dialog>

  <el-dialog v-model="isOpenValidationError" width="90%" center :show-close="false" :close-on-click-modal="false">
    {{ $t(validationError) }}
    <template #footer>
      <div class="text-center">
        <el-button
          class="full-w-button el-button--primary is-small"
          type="primary"
          @click="isOpenValidationError = false"
        >
          {{ $t('OK') }}
          <KeyenceHTHardKeyButton
            v-if="isEnabledBarcodeReadWithKeyenceHT"
            :hardkey="KeyenceHardKey.ENTER"
            :function="() => (isOpenValidationError = false)"
            :visible="isOpenValidationError"
            :newLayer="true"
          />
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import KeyenceHTHardKeyButton from '@/components/keyenceHT/hardkeys/KeyenceHTHardKeyButton.vue'
import KeyenceHTScannerMixin from '@/components/mixins/KeyenceHTScannerMixin.vue'
import OCRComponent from '@/components/OCRComponent.vue'
import ScanComponent from '@/components/ScanComponent.vue'
import IconScanQR from '@/components/svg/IconScanQR.vue'
import { SAVE_SCANNED_WORK_CENTER_CODE } from '@/store/actions'
import { LOCAL_STORAGE_ITEM } from '@/utils/constants'
import { EBCRType, ECameraReaderType } from '@/utils/enums'
import { logError } from '@/utils/errorLogger'
import { openMessage } from '@/utils/helpers'
import { KeyenceHardKey } from '@/utils/keyenceHT/keyenceHardKeyEventHandler'
import { isKeyenceEnterpriseBrowser, KeyenceModule } from '@/utils/keyenceHT/keyenceModules'
import { mixins, Options } from 'vue-class-component'
import { Prop, PropSync, Watch } from 'vue-property-decorator'
@Options({
  name: 'ActionDialog',
  emits: ['update:isShowActionDialog', 'update:confirm'],
  components: {
    OCRComponent,
    ScanComponent,
    KeyenceHTHardKeyButton,
    IconScanQR,
  },
})
export default class ActionDialogWorkCenter extends mixins(KeyenceHTScannerMixin) {
  @PropSync('isShowActionDialog', { type: Boolean }) syncedModel?: boolean
  @Prop({ default: ECameraReaderType.BCR }) actionType!: ECameraReaderType
  @Prop({ type: String }) readonly fieldName!: string
  @Prop({ default: EBCRType.DEFAULT }) customBarcodeReaderType!: EBCRType
  @Prop({ type: Boolean }) readonly isShowMultipleScan?: boolean

  scannedValue = ''
  validationError = ''
  isOpenValidationError = false
  KeyenceHardKey = KeyenceHardKey

  get currentManufacturingWRKC() {
    return this.barcode.manufacturingPlan?.workCenterCode || ''
  }

  get barcode() {
    return this.$store.state.barcode?.barcode
  }

  workCenterCodeFromStore() {
    return localStorage.getItem(LOCAL_STORAGE_ITEM.WORK_CENTER_CODE) || ''
  }

  @Watch('syncedModel')
  onDialogVisibilityChange(isOpen: boolean) {
    this.scannedValue = this.workCenterCodeFromStore()
    this.validationError = ''
    this.isOpenValidationError = false
    if (isOpen) {
      this.startKeyenceScan()
    } else {
      this.stopKeyenceScan()
    }
  }

  onDialogClosing() {
    this.syncedModel = false
    // if (this.$refs?.ocrComponent) this.$refs.ocrComponent.vidOff()
  }

  toggleKeyenceScanner() {
    if (this.isScanning) {
      this.stopKeyenceScan()
    } else {
      this.startKeyenceScan()
    }
  }

  startKeyenceScan() {
    if (this.isEnabledBarcodeReadWithKeyenceHT) {
      this.isScanning = true
      this.startScanWithKeyenceHT(this.handleKeyenceScanSuccess, undefined, false)
    }
  }

  stopKeyenceScan() {
    try {
      if (this.isEnabledBarcodeReadWithKeyenceHT && KeyenceModule && KeyenceModule.Scanner) {
        KeyenceModule.Scanner.stopRead()
        KeyenceModule.Scanner.clearReadCallback()
        this.isScanning = false
      }
    } catch (e) {
      console.error(e)
    } finally {
      this.isScanning = false
    }
  }

  handleKeyenceScanSuccess(detectedText: string) {
    openMessage(detectedText, 'success')
    const trimmedText = detectedText.trim()
    this.scannedValue = trimmedText
    this.validationError = ''
  }

  get isEnabledBarcodeReadWithKeyenceHT() {
    return isKeyenceEnterpriseBrowser()
  }

  confirmText(text: string) {
    const trimmedText = text.trim()
    this.scannedValue = trimmedText

    if (this.fieldName === 'workCenterCode') {
      if (!this.validateWorkCenterCode(trimmedText)) {
        return
      }
      this.$store.dispatch(SAVE_SCANNED_WORK_CENTER_CODE, trimmedText)
    }

    this.validationError = ''
    this.stopKeyenceScan()
    this.$emit('update:confirm', trimmedText)
  }

  validateWorkCenterCode(scannedCode: string): boolean {
    this.validationError = ''
    this.isOpenValidationError = false

    if (!scannedCode) {
      this.validationError = 'work_center_code_required'
      this.isOpenValidationError = true
      logError('', {}, '', this.$t('work_center_code_required'))
      return false
    }

    if (!scannedCode.includes(this.currentManufacturingWRKC)) {
      this.validationError = 'work_center_code_mismatch'
      this.isOpenValidationError = true
      logError('', {}, '', this.$t('work_center_code_mismatch'))
      return false
    }

    return true
  }

  resetScannedValue() {
    this.scannedValue = ''
    this.validationError = ''
    this.isOpenValidationError = false
  }

  unmounted() {
    this.stopKeyenceScan()
    this.unmountedKeyenceHTScanner()
  }

  get component() {
    switch (this.actionType) {
      case ECameraReaderType.BCR:
        return {
          item: ScanComponent,
          props: {
            fieldName: this.fieldName,
            customBarcodECameraReaderType: this.customBarcodeReaderType,
            initialValue: this.scannedValue,
            isShowMultipleScanData: this.isShowMultipleScan,
            isReadOnlyInput: true,
          },
        }
      case ECameraReaderType.OCR:
        return {
          item: OCRComponent,
          props: {
            fieldName: this.fieldName,
            initialValue: this.scannedValue,
            isShowMultipleScanData: this.isShowMultipleScan,
          },
        }
      default:
        return {
          item: ScanComponent,
          props: {
            fieldName: this.fieldName,
            customBarcodECameraReaderType: this.customBarcodeReaderType,
            initialValue: this.scannedValue,
            isShowMultipleScanData: this.isShowMultipleScan,
          },
        }
    }
  }
}
</script>

<style lang="scss" scoped>
.scan-view {
  text-align: center;
}

.keyenece-empty-scan-area {
  background: white;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 12px 0 12px;
  box-sizing: border-box;
}

.dectect-input {
  margin-top: 5px;
  padding: 0 30px 0 30px;
  text-align-last: center;
}

.step-button {
  display: flex;
  justify-content: space-evenly;
  padding-top: 10px;
}
</style>
